import { t } from "@lingui/macro"
import { FormControl } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"

import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"

interface IAgentAccessStore {
    formFields: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (...args: any[]) => string | null
    }
    isLoadingAgentTypes: boolean
    agentAccessTypes: IGenericAutoCompleteTextFieldValue[]
    agentDefinitions: import("src/api").config_ConfigDefinitionItem[]
    agentInstances: Map<string, IGenericAutoCompleteTextFieldValue[]>
    getAgentTypeValue(): IGenericAutoCompleteTextFieldValue[]
    setSelectedAgentTypes(
        agentTypes: IGenericAutoCompleteTextFieldValue[],
    ): Promise<void>
    setSelectedInstancesForAgentType(
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ): void
    fetchAgentInstances?: (agentName: string) => Promise<void>
}

interface IAgentAccessProps {
    store: IAgentAccessStore
}

export const AgentAccess = observer(({ store }: IAgentAccessProps) => {
    const error = store.formFields.error("agents")
    const isLoadingAgentTypes = store.isLoadingAgentTypes

    // Get agent type options only
    const agentOptions = useMemo(
        () => store.agentAccessTypes,
        [store.agentAccessTypes],
    )

    const handleChange = useCallback(
        async (_event, value) => {
            // For multiselect, value will be an array, but handle all cases for type safety
            const agentTypes = Array.isArray(value) ? value : []
            const previousAgentTypes = store.getAgentTypeValue()

            // Find deselected agent types
            const deselectedTypes = previousAgentTypes.filter(
                (prevType) =>
                    !agentTypes.some((newType) => newType.id === prevType.id),
            )

            // Clear instances for deselected agent types
            for (const deselectedType of deselectedTypes) {
                const agentDefinition = store.agentDefinitions.find(
                    (def) => def.config_definition_id === deselectedType.id,
                )
                if (
                    agentDefinition?.name != null &&
                    agentDefinition.name !== ""
                ) {
                    store.setSelectedInstancesForAgentType(
                        agentDefinition.name,
                        [],
                    )
                }
            }

            // Find newly selected agent types
            const newlySelectedTypes = agentTypes.filter(
                (newType) =>
                    !previousAgentTypes.some(
                        (prevType) => prevType.id === newType.id,
                    ),
            )

            // Set the selected agent types
            await store.setSelectedAgentTypes(agentTypes)

            // Handle defaults for newly selected types
            for (const newType of newlySelectedTypes) {
                const agentDefinition = store.agentDefinitions.find(
                    (def) => def.config_definition_id === newType.id,
                )

                if (
                    agentDefinition?.name != null &&
                    agentDefinition.name !== ""
                ) {
                    // Fetch instances if not already loaded
                    if (
                        !store.agentInstances.has(agentDefinition.name) &&
                        store.fetchAgentInstances != null
                    ) {
                        await store.fetchAgentInstances(agentDefinition.name)
                    }

                    const requiresInstances = Boolean(
                        (agentDefinition.metadata as Record<string, unknown>)
                            ?.agent_instance_required,
                    )

                    const instances =
                        store.agentInstances.get(agentDefinition.name) ?? []

                    if (requiresInstances) {
                        // For required instances: auto-select if only one available
                        if (instances.length === 1) {
                            store.setSelectedInstancesForAgentType(
                                agentDefinition.name,
                                instances,
                            )
                        }
                    } else {
                        // For optional instances: default to "all instances"
                        store.setSelectedInstancesForAgentType(
                            agentDefinition.name,
                            [{ id: -1, label: "all-instances" }],
                        )
                    }
                }
            }
        },
        [store],
    )

    const hasError = useMemo(() => Boolean(error), [error])

    return (
        <FormControl sx={{ breakInside: "avoid" }}>
            <AutoCompleteTextField
                multiple
                disableCloseOnSelect
                options={agentOptions}
                value={store.getAgentTypeValue()}
                placeholder={t`knowledge-item-modal.agent-access`}
                onChange={handleChange}
                loading={isLoadingAgentTypes}
                error={hasError}
                helperText={error ?? undefined}
            />
        </FormControl>
    )
})
